import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useMemo, useEffect } from "react";
import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/use-debounce";
import { useLocation, useSearch } from "wouter";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, CreditCard, RefreshCw, ArrowUpDown, Calendar, ChevronDown, Loader2 } from "lucide-react";
import { format, subDays, startOfDay, startOfMonth, startOfYear } from "date-fns";
import type { WorkOrderPayment } from "@shared/schema";

type DateRangeOption = "all" | "today" | "7days" | "30days" | "thisMonth" | "thisYear";

const dateRangeOptions: { value: DateRangeOption; label: string }[] = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7days", label: "Last 7 Days" },
  { value: "30days", label: "Last 30 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "thisYear", label: "This Year" },
];

const STATUS_OPTIONS = [
  { value: "draft",          label: "Draft" },
  { value: "pending",        label: "Pending" },
  { value: "paid",           label: "Paid" },
  { value: "partial",        label: "Partial" },
  { value: "overdue",        label: "Overdue" },
  { value: "voided",         label: "Voided" },
  { value: "uncollectible",  label: "Uncollectible" },
];

function getDateRangeStart(option: DateRangeOption): Date | null {
  const now = new Date();
  switch (option) {
    case "today":     return startOfDay(now);
    case "7days":     return subDays(now, 7);
    case "30days":    return subDays(now, 30);
    case "thisMonth": return startOfMonth(now);
    case "thisYear":  return startOfYear(now);
    default:          return null;
  }
}

type PaymentWithWorkOrder = WorkOrderPayment & {
  workOrder: {
    id: number;
    workOrderNumber: string;
    serviceType: string;
    companyId: number;
    companyName: string;
  };
};

const statusColors: Record<string, string> = {
  draft:         "bg-gray-100 text-gray-800",
  pending:       "bg-yellow-100 text-yellow-800",
  paid:          "bg-green-100 text-green-800",
  partial:       "bg-blue-100 text-blue-800",
  overdue:       "bg-red-100 text-red-800",
  voided:        "bg-gray-200 text-gray-600",
  uncollectible: "bg-red-200 text-red-800",
};

type SortField = "date" | "amount" | "status" | "company";
type SortDirection = "asc" | "desc";

export default function PaymentsPage() {
  usePageTitle("Payments");
  const [location, setLocation] = useLocation();
  const searchString = useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const p = new URLSearchParams(window.location.search);
    const s = p.get("status");
    if (s) { const arr = s.split(",").filter(Boolean); if (arr.length > 0) return arr; }
    return ["paid", "partial"];
  });
  const [companyFilter, setCompanyFilter] = useState<string>("all");

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const status = params.get("status");
    if (status) {
      const statuses = status.split(",").filter(Boolean);
      if (statuses.length > 0) setStatusFilter(statuses);
    } else {
      setStatusFilter(["paid", "partial"]);
    }
  }, [searchString]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter.length > 0 && statusFilter.length < STATUS_OPTIONS.length) {
      params.set("status", statusFilter.join(","));
    }
    const newSearch = params.toString();
    if (newSearch !== searchString) {
      setLocation(`${location}${newSearch ? `?${newSearch}` : ''}`, { replace: true } as any);
    }
  }, [statusFilter, searchString, location, setLocation]);
  const [dateRange, setDateRange] = useState<DateRangeOption>("all");
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const serverPaymentStatus = useMemo(() =>
    statusFilter.length > 0 && statusFilter.length < STATUS_OPTIONS.length ? statusFilter : undefined,
    [statusFilter]
  );
  const serverPaymentCompany = companyFilter !== 'all' ? companyFilter : undefined;

  const {
    data: paymentsInfinite,
    isLoading,
    isError,
    refetch,
    fetchNextPage: paymentsFetchNextPage,
    hasNextPage: paymentsHasNextPage,
    isFetchingNextPage: paymentsIsFetchingNextPage,
  } = useInfiniteQuery<{ data: PaymentWithWorkOrder[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ["/api/invoices", { search: debouncedSearch, status: serverPaymentStatus, companyId: serverPaymentCompany }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (debouncedSearch) qs.set('search', debouncedSearch);
      if (serverPaymentStatus) serverPaymentStatus.forEach(s => qs.append('status', s));
      if (serverPaymentCompany) qs.set('companyId', serverPaymentCompany);
      if (pageParam) qs.set('cursor', pageParam as string);
      const res = await fetch(`/api/invoices${qs.toString() ? `?${qs.toString()}` : ''}`, { credentials: 'include' });
      // A 401/500 body has no `data` array. Throw so React Query reports an
      // error instead of caching it as a page (DOTTIE-FRONTEND-N).
      if (!res.ok) throw new Error(`Failed to load payments (${res.status})`);
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  const payments = useMemo(() =>
    paymentsInfinite?.pages.flatMap(p => p.data ?? []) ?? [],
    [paymentsInfinite]
  );
  const paymentsTotalCount = paymentsInfinite?.pages[0]?.totalCount ?? payments.length;

  const companies = useMemo(() => {
    const uniqueCompanies = new Map<number, string>();
    payments.forEach((p) => {
      if (p.workOrder?.companyId && p.workOrder?.companyName) {
        uniqueCompanies.set(p.workOrder.companyId, p.workOrder.companyName);
      }
    });
    return Array.from(uniqueCompanies, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [payments]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const toggleStatus = (value: string) => {
    setStatusFilter((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };

  const toggleAllStatuses = () => {
    if (statusFilter.length === STATUS_OPTIONS.length) {
      setStatusFilter([]);
    } else {
      setStatusFilter(STATUS_OPTIONS.map((s) => s.value));
    }
  };

  const statusFilterLabel = useMemo(() => {
    if (statusFilter.length === 0 || statusFilter.length === STATUS_OPTIONS.length) {
      return "All Statuses";
    }
    if (statusFilter.length === 1) {
      return STATUS_OPTIONS.find((s) => s.value === statusFilter[0])?.label ?? statusFilter[0];
    }
    if (statusFilter.length === 2) {
      return statusFilter
        .map((v) => STATUS_OPTIONS.find((s) => s.value === v)?.label ?? v)
        .join(" & ");
    }
    return `${statusFilter.length} Statuses`;
  }, [statusFilter]);

  const filteredAndSortedPayments = useMemo(() => {
    // Status, company, and search filters are handled server-side.
    // Date filter uses lastSyncedAt (not createdAt) so stays client-side.
    const dateStart = getDateRangeStart(dateRange);

    let filtered = payments.filter((payment) => {
      const paymentDate =
        payment.status === "paid" || payment.status === "partial"
          ? payment.lastSyncedAt
          : null;
      const matchesDate = !dateStart || (paymentDate && new Date(paymentDate) >= dateStart);
      return matchesDate;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "date":
          comparison =
            new Date(a.lastSyncedAt || 0).getTime() - new Date(b.lastSyncedAt || 0).getTime();
          break;
        case "amount":
          comparison = parseFloat(a.amountDue || "0") - parseFloat(b.amountDue || "0");
          break;
        case "status":
          comparison = (a.status || "").localeCompare(b.status || "");
          break;
        case "company":
          comparison = (a.workOrder?.companyName || "").localeCompare(
            b.workOrder?.companyName || ""
          );
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [payments, searchQuery, dateRange, sortField, sortDirection]);

  const summary = useMemo(() => {
    const totalPaid = filteredAndSortedPayments.reduce(
      (acc, p) => acc + parseFloat(p.amountPaid || "0"),
      0
    );
    const totalDue = filteredAndSortedPayments.reduce(
      (acc, p) => acc + parseFloat(p.amountDue || "0"),
      0
    );
    const totalBalance = filteredAndSortedPayments.reduce(
      (acc, p) => acc + parseFloat(p.unpaidBalance || "0"),
      0
    );
    return { totalPaid, totalDue, totalBalance, count: filteredAndSortedPayments.length };
  }, [filteredAndSortedPayments]);

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <button
      className="inline-flex items-center gap-1 font-medium cursor-pointer bg-transparent border-0 p-0 text-inherit text-sm leading-none"
      onClick={() => toggleSort(field)}
    >
      {label}
      <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  return (
    <ResponsiveLayout title="Payments" subtitle="Track payment status and history">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
            <p className="text-gray-500">Track payment status and history</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Due</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${summary.totalDue.toFixed(2)}</div>
              <p className="text-xs text-gray-500">{summary.count} payments</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">${summary.totalPaid.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Outstanding Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">${summary.totalBalance.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Collection Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {summary.totalDue > 0
                  ? ((summary.totalPaid / summary.totalDue) * 100).toFixed(1)
                  : "0"}
                %
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search payments, work orders, companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="input-search"
                />
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[180px] justify-between font-normal"
                    data-testid="select-status"
                  >
                    <span className="truncate">{statusFilterLabel}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[180px] p-2" align="start">
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm font-medium">
                      <Checkbox
                        checked={statusFilter.length === STATUS_OPTIONS.length}
                        onCheckedChange={toggleAllStatuses}
                      />
                      All Statuses
                    </label>
                    <div className="border-t my-1" />
                    {STATUS_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={statusFilter.includes(option.value)}
                          onCheckedChange={() => toggleStatus(option.value)}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className="w-[200px]" data-testid="select-company">
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id.toString()}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRangeOption)}>
                <SelectTrigger className="w-[160px]" data-testid="select-date-range">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  {dateRangeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isError && payments.length > 0 && (
              <div
                role="alert"
                className="mb-4 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
              >
                <span>Couldn't refresh payments. These rows may be out of date.</span>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Try again
                </Button>
              </div>
            )}
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading payments...</div>
            ) : isError && payments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Payments couldn't be loaded.</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
                  Try again
                </Button>
              </div>
            ) : filteredAndSortedPayments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No payments found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payment ID</TableHead>
                    <TableHead>Work Order</TableHead>
                    <TableHead>
                      <SortButton field="company" label="Company" />
                    </TableHead>
                    <TableHead>
                      <SortButton field="status" label="Status" />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortButton field="amount" label="Amount Due" />
                    </TableHead>
                    <TableHead className="text-right">Amount Paid</TableHead>
                    <TableHead className="text-right">Stripe Fee</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>
                      <SortButton field="date" label="Payment Date" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedPayments.map((payment) => (
                    <TableRow key={payment.id} data-testid={`row-payment-${payment.id}`}>
                      <TableCell className="font-mono text-sm">
                        #{payment.id}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="link"
                          className="p-0 h-auto"
                          onClick={() => setLocation(`/work-orders/${payment.workOrderId}`)}
                          data-testid={`link-workorder-${payment.workOrderId}`}
                        >
                          {payment.workOrder?.workOrderNumber || "-"}
                        </Button>
                      </TableCell>
                      <TableCell>{payment.workOrder?.companyName || "-"}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[payment.status] || "bg-gray-100"}>
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        ${parseFloat(payment.amountDue || "0").toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        ${parseFloat(payment.amountPaid || "0").toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {(payment as any).feeTotal ? `$${parseFloat((payment as any).feeTotal).toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ${parseFloat(payment.unpaidBalance || "0").toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {(payment.status === "paid" || payment.status === "partial") && payment.lastSyncedAt
                          ? format(new Date(payment.lastSyncedAt), "MMM d, yyyy")
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="flex flex-col items-center gap-2 mt-4 px-4 pb-4">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Showing {payments.length} of {paymentsTotalCount}
        </p>
        {!isLoading && paymentsHasNextPage && (
          <Button variant="outline" onClick={() => paymentsFetchNextPage()} disabled={paymentsIsFetchingNextPage}
            aria-label="Load more payments" aria-busy={paymentsIsFetchingNextPage}>
            {paymentsIsFetchingNextPage ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
            ) : "Load more"}
          </Button>
        )}
      </div>
    </ResponsiveLayout>
  );
}
